/**
 * useDownloadIncidentReport (P356) -- fetch the signed incident-report
 * pack (zip) for one finding as a Blob + trigger a browser download.
 * Mirrors useDownloadWarRoomPack (manual bearer; bypasses api.request's
 * JSON parse).
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { currentAuthToken } from "shared/api/client";

export class IncidentReportDownloadError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
    this.name = "IncidentReportDownloadError";
  }
}

function _filename(header: string | null, findingId: string): string {
  const fallback = `marsad-incident-report-${findingId}.zip`;
  if (!header) return fallback;
  const match = header.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? fallback;
}

async function _download(vars: { findingId: string }): Promise<{ filename: string }> {
  const token = currentAuthToken();
  const url = `/v1/findings/${encodeURIComponent(vars.findingId)}/incident-report-pack.zip`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/zip",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      detail = (await response.json())?.detail ?? detail;
    } catch {
      // non-JSON body; keep statusText
    }
    throw new IncidentReportDownloadError(response.status, detail);
  }
  const filename = _filename(
    response.headers.get("Content-Disposition"),
    vars.findingId,
  );
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
  return { filename };
}

export function useDownloadIncidentReport(): UseMutationResult<
  { filename: string },
  IncidentReportDownloadError,
  { findingId: string }
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: _download,
  });
}
